import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Info, CheckCircle, X, AlertCircle } from 'lucide-react';

interface Alert {
  id: string;
  type: 'warning' | 'error' | 'info' | 'success';
  title: string;
  message: string;
  timestamp?: Date;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface AlertBannerProps {
  alerts: Alert[];
  onDismiss?: (id: string) => void;
  maxVisible?: number;
}

const AlertBanner: React.FC<AlertBannerProps> = ({ 
  alerts, 
  onDismiss,
  maxVisible = 3 
}) => {
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  const visibleAlerts = alerts
    .filter(alert => !dismissedAlerts.has(alert.id))
    .slice(0, maxVisible);

  const handleDismiss = (id: string) => {
    setDismissedAlerts(prev => new Set([...prev, id]));
    onDismiss?.(id);
  };

  const getAlertStyles = (type: Alert['type']) => {
    switch (type) {
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: 'text-red-600',
          text: 'text-red-800',
          button: 'hover:bg-red-100'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: 'text-yellow-600',
          text: 'text-yellow-800',
          button: 'hover:bg-yellow-100'
        };
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: 'text-blue-600',
          text: 'text-blue-800',
          button: 'hover:bg-blue-100'
        };
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          icon: 'text-green-600',
          text: 'text-green-800',
          button: 'hover:bg-green-100'
        };
    }
  };

  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="h-5 w-5" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5" />;
      case 'info':
        return <Info className="h-5 w-5" />;
      case 'success':
        return <CheckCircle className="h-5 w-5" />;
    }
  };

  if (visibleAlerts.length === 0) return null;

  return (
    <div className="space-y-3">
      <AnimatePresence>
        {visibleAlerts.map((alert, index) => {
          const styles = getAlertStyles(alert.type);
          
          return (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.95 }}
              transition={{ 
                duration: 0.3,
                delay: index * 0.1
              }}
              className={`
                ${styles.bg} ${styles.border} border rounded-lg p-4
                shadow-sm hover:shadow-md transition-shadow
              `}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 ${styles.icon}`}>
                  {getAlertIcon(alert.type)}
                </div>
                <div className="ml-3 flex-1">
                  <h3 className={`text-sm font-medium ${styles.text}`}>
                    {alert.title}
                  </h3>
                  <div className={`mt-1 text-sm ${styles.text} opacity-90`}>
                    {alert.message}
                  </div>
                  {alert.action && (
                    <div className="mt-3">
                      <button
                        onClick={alert.action.onClick}
                        className={`
                          text-sm font-medium ${styles.icon} 
                          hover:underline focus:outline-none
                        `}
                      >
                        {alert.action.label} →
                      </button>
                    </div>
                  )}
                  {alert.timestamp && (
                    <p className="mt-2 text-xs text-gray-500">
                      {new Date(alert.timestamp).toLocaleTimeString()}
                    </p>
                  )}
                </div>
                <div className="ml-4 flex-shrink-0">
                  <button
                    onClick={() => handleDismiss(alert.id)}
                    className={`
                      inline-flex rounded-md p-1.5 ${styles.text} ${styles.button}
                      focus:outline-none focus:ring-2 focus:ring-offset-2 
                      focus:ring-offset-${alert.type === 'error' ? 'red' : 
                        alert.type === 'warning' ? 'yellow' : 
                        alert.type === 'info' ? 'blue' : 'green'}-50
                      focus:ring-${alert.type === 'error' ? 'red' : 
                        alert.type === 'warning' ? 'yellow' : 
                        alert.type === 'info' ? 'blue' : 'green'}-600
                    `}
                  >
                    <span className="sr-only">Dismiss</span>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      
      {alerts.length > maxVisible && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm text-gray-500 text-center"
        >
          And {alerts.length - maxVisible} more alerts...
        </motion.p>
      )}
    </div>
  );
};

export default AlertBanner;